import React, { useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, TextField, Chip, IconButton, Tooltip,
  FormControl, InputLabel, Select, MenuItem, Divider, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody,
  TableCell, TableHead, TableRow, Tabs, Tab
} from '@material-ui/core';
import {
  CloudUpload, Add, Room as PlaceIcon, CheckCircle, SkipNext,
  ExpandMore, ExpandLess, Delete, PlayArrow, Edit as EditIcon
} from '@material-ui/icons';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { v4 as uuidv4 } from 'uuid';
import { PLACEMENT_ITEM_TYPES } from './siteTakeoffConstants';

// Simple CSV parser — handles quoted fields
const parseCSV = (text) => {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };

  const parseLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if ((ch === ',' || ch === '\t') && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseLine(lines[0]);
  const rows = lines.slice(1).map((line, idx) => {
    const values = parseLine(line);
    const row = {};
    headers.forEach((h, i) => { row[h] = values[i] || ''; });
    row._rowNum = idx + 1;
    return row;
  });
  return { headers, rows };
};

// Parse a numeric value from messy input like "30''" or "55'" or "16"
const parseNumeric = (val) => {
  if (!val) return 0;
  const cleaned = String(val).replace(/['"ftinm\s]/gi, '').replace(/[^0-9.]/g, '');
  return parseFloat(cleaned) || 0;
};

// Size patterns commonly found in plan legends
const SIZE_PATTERN = /(\d+["']\s*(?:Box|box|Cal|cal)?|\d+\s*(?:Gallon|gal|Gal)|#\s*\d+|\d+["']\s*(?:ht|HT|Ht)|(?:Box|Flat)\s*\w*|\d+'\s*(?:x\s*\d+'?\s*)?(?:clr|clear|CLR)?)/i;
const QTY_PATTERN = /\b(\d{1,4})\b/;

/**
 * Parse freeform text copied from a plan legend.
 * Handles typical formats:
 *   "Chilopsis linearis Art's Seedless®  24" Box  5"
 *   "SEEDLESS DESERT WILLOW"
 *   "Bougainvillea hybrid 'Monka'  3 Gallon  6  1.0 GPH"
 *
 * Returns array of { species, commonName, size, qty, notes }
 */
const parsePlanText = (text, itemType) => {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
  const items = [];
  let currentItem = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Skip header-like lines
    if (/^(SYMBOL|LANDSCAPE LEGEND|IRRIGATION LEGEND|HARDSCAPE|NOTES|SHRUBS|TREES|ACCENTS|GROUNDCOVER|MATERIALS)/i.test(line)) continue;
    if (/^(SYMBOL\s+NAME\s+SIZE|QTY)/i.test(line)) continue;

    // Detect if this line has a quantity (likely a data line vs a common-name-only line)
    const hasSize = SIZE_PATTERN.test(line);
    // Split line into chunks by multiple spaces or tabs
    const chunks = line.split(/\t|\s{2,}/).map(c => c.trim()).filter(c => c.length > 0);

    if (chunks.length >= 2 && (hasSize || /\d/.test(line))) {
      // This looks like a data line with species/size/qty
      // Try to extract: first chunk = species name, then find size and qty
      let species = chunks[0];
      let size = '';
      let qty = 0;
      let notes = '';

      for (let j = 1; j < chunks.length; j++) {
        const chunk = chunks[j];
        if (SIZE_PATTERN.test(chunk) && !size) {
          size = chunk;
        } else if (/^\d{1,4}$/.test(chunk) && !qty) {
          qty = parseInt(chunk, 10);
        } else if (/GPH|GPM|Per Tree|Per Shrub/i.test(chunk)) {
          notes = chunk;
        } else if (!size && !qty) {
          // Might be part of species name
          species += ' ' + chunk;
        } else {
          if (notes) notes += ' ' + chunk;
          else notes = chunk;
        }
      }

      if (currentItem) {
        items.push(currentItem);
      }

      currentItem = { species, commonName: '', size, qty, notes };
    } else if (currentItem && chunks.length === 1 && /^[A-Z\s''-]+$/.test(line) && line.length < 60) {
      // All-caps single line after a data line — likely common name
      currentItem.commonName = line.split(/\s+/).map(w =>
        w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
      ).join(' ');
    } else if (chunks.length >= 1 && !currentItem) {
      // First line with no numbers — might be a species with data on same line
      if (currentItem) items.push(currentItem);
      currentItem = { species: line, commonName: '', size: '', qty: 0, notes: '' };
    }
  }
  if (currentItem) items.push(currentItem);

  // Build queue items from parsed data
  return items.filter(it => it.species || it.commonName).map(it => {
    const displayName = it.commonName || it.species;
    const details = {};

    if (itemType === 'tree') {
      details.species = displayName;
      details.caliper = 0;
      details.height = 0;
      details.canopySpread = 0;
      details.condition = '';
      details.disposition = 'new';
      if (it.size) details.species += ` (${it.size})`;
    } else if (itemType === 'boulder') {
      details.material = it.species;
      details.size = it.size;
      details.qty = it.qty;
      details.notes = it.notes;
    } else {
      // plant or other
      details.species = displayName;
      details.size = it.size;
      details.qty = it.qty;
      details.spacing = '';
      details.container = '';
    }

    const typeDef = PLACEMENT_ITEM_TYPES[itemType];
    return {
      species: displayName,
      size: it.size,
      qty: it.qty,
      notes: it.notes,
      details,
      label: typeDef ? typeDef.buildLabel(details) : displayName,
    };
  });
};

const PlacementQueue = ({ queue, onQueueChange, onStartPlacing, placingItemId, plantSizes = [] }) => {
  const [expanded, setExpanded] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [importType, setImportType] = useState('tree');
  const [csvText, setCsvText] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [columnMap, setColumnMap] = useState({});
  const [importStep, setImportStep] = useState(0); // 0=paste, 1=map columns, 2=preview
  const [importTab, setImportTab] = useState(0); // 0=CSV, 1=Paste from Plans, 2=Build List
  const [planText, setPlanText] = useState('');
  const [parsedPlanItems, setParsedPlanItems] = useState([]);
  const [manualItems, setManualItems] = useState([]);
  const fileInputRef = useRef(null);

  const unplaced = queue.filter(q => q.status === 'unplaced');
  const placed = queue.filter(q => q.status === 'placed');
  const skipped = queue.filter(q => q.status === 'skipped');

  // ===== CSV IMPORT =====
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (evt) => {
      setCsvText(evt.target.result);
      const data = parseCSV(evt.target.result);
      setParsedData(data);
      // Auto-map columns by fuzzy matching field labels
      autoMapColumns(data.headers, importType);
      setImportStep(1);
    };
    reader.readAsText(file);
  }, [importType]);

  const handlePasteCSV = useCallback(() => {
    if (!csvText.trim()) return;
    const data = parseCSV(csvText);
    setParsedData(data);
    autoMapColumns(data.headers, importType);
    setImportStep(1);
  }, [csvText, importType]);

  const autoMapColumns = (headers, type) => {
    const typeDef = PLACEMENT_ITEM_TYPES[type];
    if (!typeDef) return;
    const map = {};
    const lowerHeaders = headers.map(h => h.toLowerCase().replace(/[^a-z0-9]/g, ''));

    typeDef.fields.forEach(field => {
      const fieldKey = field.key.toLowerCase();
      const fieldLabel = field.label.toLowerCase().replace(/[^a-z0-9]/g, '');
      // Try exact match, then partial
      let idx = lowerHeaders.findIndex(h => h === fieldKey || h === fieldLabel);
      if (idx === -1) idx = lowerHeaders.findIndex(h => h.includes(fieldKey) || fieldKey.includes(h));
      if (idx === -1 && field.key === 'species') {
        // Try common variants
        idx = lowerHeaders.findIndex(h => h.includes('common') || h.includes('scientific') || h.includes('name'));
      }
      if (idx >= 0) map[field.key] = headers[idx];
    });
    setColumnMap(map);
  };

  const handleImport = useCallback(() => {
    if (!parsedData?.rows?.length) return;
    const typeDef = PLACEMENT_ITEM_TYPES[importType];
    if (!typeDef) return;

    const newItems = parsedData.rows.map((row, idx) => {
      const details = {};
      typeDef.fields.forEach(field => {
        const csvCol = columnMap[field.key];
        if (csvCol && row[csvCol] !== undefined) {
          details[field.key] = field.type === 'number' ? parseNumeric(row[csvCol]) : row[csvCol];
        }
      });
      // Also store any extra columns as raw data
      details._raw = { ...row };
      delete details._raw._rowNum;

      return {
        queueId: uuidv4(),
        itemType: importType,
        label: typeDef.buildLabel(details),
        details,
        status: 'unplaced',
        areaId: null,
        sourceRow: row._rowNum,
      };
    });

    onQueueChange([...queue, ...newItems]);
    setImportOpen(false);
    setImportStep(0);
    setCsvText('');
    setParsedData(null);
    setColumnMap({});
  }, [parsedData, importType, columnMap, queue, onQueueChange]);

  // ===== PASTE FROM PLANS =====
  const handleParsePlanText = useCallback(() => {
    if (!planText.trim()) return;
    const items = parsePlanText(planText, importType);
    setParsedPlanItems(items);
  }, [planText, importType]);

  const handleImportPlanItems = useCallback(() => {
    if (!parsedPlanItems.length) return;
    const typeDef = PLACEMENT_ITEM_TYPES[importType];
    if (!typeDef) return;

    const newItems = parsedPlanItems.map((item, idx) => ({
      queueId: uuidv4(),
      itemType: importType,
      label: item.label,
      details: item.details,
      status: 'unplaced',
      areaId: null,
      sourceRow: idx + 1,
    }));

    // Expand qty > 1 into individual items for placement
    const expanded = [];
    newItems.forEach(item => {
      const qty = item.details?.qty || 1;
      for (let i = 0; i < qty; i++) {
        expanded.push({
          ...item,
          queueId: uuidv4(),
          label: qty > 1 ? `${item.label} (${i + 1}/${qty})` : item.label,
        });
      }
    });

    onQueueChange([...queue, ...expanded]);
    setImportOpen(false);
    setPlanText('');
    setParsedPlanItems([]);
  }, [parsedPlanItems, importType, queue, onQueueChange]);

  // ===== BUILD LIST (manual table) =====
  const handleAddManualRow = useCallback(() => {
    const typeDef = PLACEMENT_ITEM_TYPES[importType];
    if (!typeDef) return;
    setManualItems(prev => [...prev, { id: uuidv4(), species: '', size: '', qty: 1, notes: '' }]);
  }, [importType]);

  const handleUpdateManualRow = useCallback((id, field, value) => {
    setManualItems(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  }, []);

  const handleRemoveManualRow = useCallback((id) => {
    setManualItems(prev => prev.filter(r => r.id !== id));
  }, []);

  const handleImportManualItems = useCallback(() => {
    if (!manualItems.length) return;
    const typeDef = PLACEMENT_ITEM_TYPES[importType];
    if (!typeDef) return;

    const expanded = [];
    manualItems.forEach((row) => {
      const qty = parseInt(row.qty, 10) || 1;
      const details = {};

      if (importType === 'tree') {
        details.species = row.species;
        details.caliper = 0;
        details.height = 0;
        details.canopySpread = 0;
        details.condition = '';
        details.disposition = 'new';
      } else if (importType === 'boulder') {
        details.material = row.species;
        details.size = row.size;
        details.qty = qty;
        details.notes = row.notes;
      } else {
        details.species = row.species;
        details.size = row.size;
        details.qty = qty;
      }

      const label = typeDef.buildLabel(details);
      for (let i = 0; i < qty; i++) {
        expanded.push({
          queueId: uuidv4(),
          itemType: importType,
          label: qty > 1 ? `${label} (${i + 1}/${qty})` : label,
          details,
          status: 'unplaced',
          areaId: null,
          sourceRow: null,
        });
      }
    });

    onQueueChange([...queue, ...expanded]);
    setImportOpen(false);
    setManualItems([]);
  }, [manualItems, importType, queue, onQueueChange]);

  // ===== MANUAL ADD (single item) =====
  const handleAddManual = useCallback(() => {
    const typeDef = PLACEMENT_ITEM_TYPES[importType];
    if (!typeDef) return;
    const newItem = {
      queueId: uuidv4(),
      itemType: importType || 'tree',
      label: typeDef.label,
      details: {},
      status: 'unplaced',
      areaId: null,
      sourceRow: null,
    };
    onQueueChange([...queue, newItem]);
  }, [importType, queue, onQueueChange]);

  // ===== ACTIONS =====
  const handleSkip = useCallback((queueId) => {
    onQueueChange(queue.map(q => q.queueId === queueId ? { ...q, status: 'skipped' } : q));
  }, [queue, onQueueChange]);

  const handleUnskip = useCallback((queueId) => {
    onQueueChange(queue.map(q => q.queueId === queueId ? { ...q, status: 'unplaced' } : q));
  }, [queue, onQueueChange]);

  const handleRemoveItem = useCallback((queueId) => {
    onQueueChange(queue.filter(q => q.queueId !== queueId));
  }, [queue, onQueueChange]);

  const handleClearAll = useCallback(() => {
    if (!window.confirm(`Remove all ${queue.length} items from the placement queue?`)) return;
    onQueueChange([]);
  }, [queue, onQueueChange]);

  const typeDef = PLACEMENT_ITEM_TYPES[importType] || PLACEMENT_ITEM_TYPES.tree;

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex" alignItems="center" justifyContent="space-between"
        style={{ cursor: 'pointer', padding: '4px 0' }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="caption" style={{ fontWeight: 600 }}>
          Placement Queue {queue.length > 0 ? `(${unplaced.length} to place / ${placed.length} placed)` : ''}
        </Typography>
        {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </Box>

      <Collapse in={expanded}>
        {/* Actions bar */}
        <Box display="flex" style={{ gap: 4, marginBottom: 6 }} flexWrap="wrap">
          {placingItemId && (
            <Tooltip title="Stop placing — deactivate map clicking">
              <Button
                size="small" variant="contained"
                style={{ fontSize: '0.72rem', textTransform: 'none', backgroundColor: '#e65100', color: '#fff', minWidth: 'auto' }}
                onClick={() => onStartPlacing(null)}
              >
                Stop Placing
              </Button>
            </Tooltip>
          )}
          <Button
            size="small" variant="outlined" startIcon={<CloudUpload />}
            style={{ fontSize: '0.72rem', textTransform: 'none' }}
            onClick={() => { onStartPlacing(null); setImportOpen(true); setImportStep(0); setCsvText(''); setParsedData(null); }}
          >
            Import
          </Button>
          <FormControl variant="outlined" size="small" style={{ minWidth: 100 }}>
            <Select
              value={importType}
              onChange={(e) => setImportType(e.target.value)}
              style={{ fontSize: '0.72rem', height: 30 }}
            >
              {Object.entries(PLACEMENT_ITEM_TYPES).map(([key, def]) => (
                <MenuItem key={key} value={key}>{def.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Add one item manually">
            <IconButton size="small" onClick={handleAddManual} style={{ border: '1px solid #ccc', borderRadius: 4, padding: 4 }}>
              <Add style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {queue.length > 0 && (
            <Tooltip title="Clear all">
              <IconButton size="small" onClick={handleClearAll} style={{ padding: 4 }}>
                <Delete style={{ fontSize: 16, color: '#999' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Unplaced items */}
        {unplaced.length > 0 && (
          <Box mb={1}>
            <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
              Unplaced ({unplaced.length})
            </Typography>
            {unplaced.map((item) => {
              const td = PLACEMENT_ITEM_TYPES[item.itemType];
              const isPlacing = placingItemId === item.queueId;
              return (
                <Box
                  key={item.queueId}
                  display="flex" alignItems="center"
                  style={{
                    padding: '4px 6px', marginBottom: 2, borderRadius: 4,
                    border: isPlacing ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    backgroundColor: isPlacing ? '#e3f2fd' : '#fff',
                    cursor: 'pointer',
                  }}
                  onClick={() => onStartPlacing(item.queueId)}
                >
                  <div style={{
                    width: 10, height: 10, borderRadius: '50%',
                    backgroundColor: td?.color || '#999', marginRight: 6, flexShrink: 0,
                    border: '1px solid rgba(0,0,0,0.15)',
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.label}
                    </div>
                    {item.sourceRow && (
                      <div style={{ fontSize: '0.65rem', color: '#999' }}>Row {item.sourceRow}</div>
                    )}
                  </div>
                  {isPlacing ? (
                    <Chip size="small" label="Click map" style={{ height: 20, fontSize: '0.65rem', backgroundColor: '#1976d2', color: '#fff' }} />
                  ) : (
                    <Tooltip title="Place on map">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onStartPlacing(item.queueId); }} style={{ padding: 2 }}>
                        <PlaceIcon style={{ fontSize: 16, color: '#1976d2' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Skip">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSkip(item.queueId); }} style={{ padding: 2 }}>
                      <SkipNext style={{ fontSize: 14, color: '#999' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Placed items */}
        {placed.length > 0 && (
          <Box mb={1}>
            <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.7rem', fontWeight: 600, color: '#4caf50' }}>
              Placed ({placed.length})
            </Typography>
            {placed.map((item) => (
              <Box key={item.queueId} display="flex" alignItems="center" style={{ padding: '2px 6px', opacity: 0.7 }}>
                <CheckCircle style={{ fontSize: 14, color: '#4caf50', marginRight: 6 }} />
                <Typography variant="caption" style={{ flex: 1, fontSize: '0.72rem' }}>{item.label}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Skipped items */}
        {skipped.length > 0 && (
          <Box mb={1}>
            <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
              Skipped ({skipped.length})
            </Typography>
            {skipped.map((item) => (
              <Box key={item.queueId} display="flex" alignItems="center" style={{ padding: '2px 6px', opacity: 0.5 }}>
                <SkipNext style={{ fontSize: 14, color: '#999', marginRight: 6 }} />
                <Typography variant="caption" style={{ flex: 1, fontSize: '0.72rem', textDecoration: 'line-through' }}>{item.label}</Typography>
                <Tooltip title="Restore">
                  <IconButton size="small" onClick={() => handleUnskip(item.queueId)} style={{ padding: 2 }}>
                    <PlayArrow style={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}

        {queue.length === 0 && (
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', textAlign: 'center', padding: 8, fontSize: '0.72rem' }}>
            Import a CSV or add items to place on the map.
          </Typography>
        )}
      </Collapse>

      {/* ===== IMPORT DIALOG ===== */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle style={{ paddingBottom: 0 }}>
          Import {typeDef.label} List
        </DialogTitle>
        <Tabs
          value={importTab}
          onChange={(e, v) => { setImportTab(v); setImportStep(0); setParsedData(null); setParsedPlanItems([]); }}
          indicatorColor="primary"
          textColor="primary"
          style={{ paddingLeft: 24, borderBottom: '1px solid #eee' }}
        >
          <Tab label="CSV Import" style={{ textTransform: 'none', minHeight: 36 }} />
          <Tab label="Paste from Plans" style={{ textTransform: 'none', minHeight: 36 }} />
          <Tab label="Build List" style={{ textTransform: 'none', minHeight: 36 }} />
        </Tabs>
        <DialogContent>
          {/* ---- TAB 0: CSV Import (existing) ---- */}
          {importTab === 0 && importStep === 0 && (
            <Box>
              <Typography variant="body2" gutterBottom>
                Paste CSV/tab-delimited data below, or upload a .csv file.
              </Typography>
              <TextField
                multiline rows={8} fullWidth variant="outlined"
                placeholder="Paste CSV data here (with headers)..."
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                style={{ marginBottom: 8 }}
              />
              <Box display="flex" style={{ gap: 8 }}>
                <Button variant="outlined" onClick={handlePasteCSV} disabled={!csvText.trim()}>
                  Parse Pasted Data
                </Button>
                <Button variant="outlined" startIcon={<CloudUpload />} onClick={() => fileInputRef.current?.click()}>
                  Upload CSV File
                </Button>
                <input type="file" accept=".csv,.tsv,.txt" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} />
              </Box>
            </Box>
          )}

          {importTab === 0 && importStep === 1 && parsedData && (
            <Box>
              <Typography variant="body2" gutterBottom>
                Found <strong>{parsedData.rows.length}</strong> rows with <strong>{parsedData.headers.length}</strong> columns. Map CSV columns to {typeDef.label} fields:
              </Typography>
              <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                {typeDef.fields.map(field => (
                  <FormControl key={field.key} variant="outlined" size="small" fullWidth>
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      label={field.label}
                      value={columnMap[field.key] || ''}
                      onChange={(e) => setColumnMap(prev => ({ ...prev, [field.key]: e.target.value }))}
                    >
                      <MenuItem value=""><em>— skip —</em></MenuItem>
                      {parsedData.headers.map(h => (
                        <MenuItem key={h} value={h}>{h}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
              </Box>

              <Divider style={{ margin: '8px 0' }} />
              <Typography variant="caption" style={{ fontWeight: 600 }}>Preview (first 5 rows):</Typography>
              <Box style={{ overflowX: 'auto', maxHeight: 200, marginTop: 4 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: 600, fontSize: '0.72rem', padding: '4px 8px' }}>#</TableCell>
                      {typeDef.fields.filter(f => columnMap[f.key]).map(f => (
                        <TableCell key={f.key} style={{ fontWeight: 600, fontSize: '0.72rem', padding: '4px 8px' }}>{f.label}</TableCell>
                      ))}
                      <TableCell style={{ fontWeight: 600, fontSize: '0.72rem', padding: '4px 8px' }}>Label</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedData.rows.slice(0, 5).map((row, idx) => {
                      const details = {};
                      typeDef.fields.forEach(f => {
                        const col = columnMap[f.key];
                        if (col) details[f.key] = f.type === 'number' ? parseNumeric(row[col]) : row[col];
                      });
                      return (
                        <TableRow key={idx}>
                          <TableCell style={{ fontSize: '0.72rem', padding: '4px 8px' }}>{row._rowNum}</TableCell>
                          {typeDef.fields.filter(f => columnMap[f.key]).map(f => (
                            <TableCell key={f.key} style={{ fontSize: '0.72rem', padding: '4px 8px' }}>
                              {f.type === 'number' ? parseNumeric(row[columnMap[f.key]]) : row[columnMap[f.key]]}
                            </TableCell>
                          ))}
                          <TableCell style={{ fontSize: '0.72rem', padding: '4px 8px', fontWeight: 500 }}>
                            {typeDef.buildLabel(details)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}

          {/* ---- TAB 1: Paste from Plans ---- */}
          {importTab === 1 && (
            <Box>
              <Typography variant="body2" gutterBottom>
                Copy the legend table from your plan PDF and paste it below. The system will try to parse species, sizes, and quantities automatically.
              </Typography>
              <TextField
                multiline rows={10} fullWidth variant="outlined"
                placeholder={"Paste plan legend text here, e.g.:\n\nChilopsis linearis Art's Seedless®    24\" Box    5\nSEEDLESS DESERT WILLOW\nPistacia X 'Red Push'    24\" Box    3\nRED PUSH PISTACHE"}
                value={planText}
                onChange={(e) => setPlanText(e.target.value)}
                style={{ marginBottom: 8, fontFamily: 'monospace', fontSize: '0.85rem' }}
              />
              <Button variant="outlined" onClick={handleParsePlanText} disabled={!planText.trim()}>
                Parse Text
              </Button>

              {parsedPlanItems.length > 0 && (
                <Box mt={2}>
                  <Typography variant="caption" style={{ fontWeight: 600 }}>
                    Parsed {parsedPlanItems.length} items (will expand to {parsedPlanItems.reduce((s, i) => s + (i.qty || 1), 0)} placement points):
                  </Typography>
                  <Box style={{ overflowX: 'auto', maxHeight: 300, marginTop: 4 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ fontWeight: 600, fontSize: '0.72rem', padding: '4px 8px' }}>Species / Item</TableCell>
                          <TableCell style={{ fontWeight: 600, fontSize: '0.72rem', padding: '4px 8px' }}>Size</TableCell>
                          <TableCell style={{ fontWeight: 600, fontSize: '0.72rem', padding: '4px 8px' }}>Qty</TableCell>
                          <TableCell style={{ fontWeight: 600, fontSize: '0.72rem', padding: '4px 8px' }}>Notes</TableCell>
                          <TableCell style={{ fontWeight: 600, fontSize: '0.72rem', padding: '4px 8px' }}>Label</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {parsedPlanItems.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell style={{ fontSize: '0.72rem', padding: '4px 8px' }}>{item.species}</TableCell>
                            <TableCell style={{ fontSize: '0.72rem', padding: '4px 8px' }}>{item.size}</TableCell>
                            <TableCell style={{ fontSize: '0.72rem', padding: '4px 8px' }}>{item.qty || '-'}</TableCell>
                            <TableCell style={{ fontSize: '0.72rem', padding: '4px 8px' }}>{item.notes}</TableCell>
                            <TableCell style={{ fontSize: '0.72rem', padding: '4px 8px', fontWeight: 500 }}>{item.label}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* ---- TAB 2: Build List ---- */}
          {importTab === 2 && (
            <Box>
              <Typography variant="body2" gutterBottom>
                Manually build a list of items. Each item with qty &gt; 1 will create that many placement points.
              </Typography>
              <Box style={{ overflowX: 'auto', maxHeight: 400 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: 600, fontSize: '0.72rem', padding: '4px 8px' }}>
                        {importType === 'boulder' ? 'Material' : 'Species / Name'}
                      </TableCell>
                      <TableCell style={{ fontWeight: 600, fontSize: '0.72rem', padding: '4px 8px', width: 100 }}>Size</TableCell>
                      <TableCell style={{ fontWeight: 600, fontSize: '0.72rem', padding: '4px 8px', width: 60 }}>Qty</TableCell>
                      <TableCell style={{ fontWeight: 600, fontSize: '0.72rem', padding: '4px 8px' }}>Notes</TableCell>
                      <TableCell style={{ width: 40, padding: '4px' }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {manualItems.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell style={{ padding: '2px 4px' }}>
                          <TextField
                            fullWidth size="small" variant="standard"
                            value={row.species}
                            onChange={(e) => handleUpdateManualRow(row.id, 'species', e.target.value)}
                            placeholder={importType === 'boulder' ? 'Painted Desert' : 'Palo Verde'}
                            InputProps={{ style: { fontSize: '0.8rem' } }}
                          />
                        </TableCell>
                        <TableCell style={{ padding: '2px 4px' }}>
                          <Autocomplete
                            freeSolo
                            options={plantSizes}
                            value={row.size || ''}
                            onChange={(e, val) => handleUpdateManualRow(row.id, 'size', val || '')}
                            onInputChange={(e, val) => handleUpdateManualRow(row.id, 'size', val || '')}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small" variant="standard"
                                placeholder={'24" Box'}
                                InputProps={{ ...params.InputProps, style: { fontSize: '0.8rem' } }}
                              />
                            )}
                            size="small"
                            style={{ minWidth: 90 }}
                          />
                        </TableCell>
                        <TableCell style={{ padding: '2px 4px' }}>
                          <TextField
                            fullWidth size="small" variant="standard" type="number"
                            value={row.qty}
                            onChange={(e) => handleUpdateManualRow(row.id, 'qty', e.target.value)}
                            InputProps={{ style: { fontSize: '0.8rem' }, inputProps: { min: 1 } }}
                          />
                        </TableCell>
                        <TableCell style={{ padding: '2px 4px' }}>
                          <TextField
                            fullWidth size="small" variant="standard"
                            value={row.notes}
                            onChange={(e) => handleUpdateManualRow(row.id, 'notes', e.target.value)}
                            InputProps={{ style: { fontSize: '0.8rem' } }}
                          />
                        </TableCell>
                        <TableCell style={{ padding: '2px 4px' }}>
                          <IconButton size="small" onClick={() => handleRemoveManualRow(row.id)} style={{ padding: 2 }}>
                            <Delete style={{ fontSize: 14, color: '#999' }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <Button
                size="small" startIcon={<Add />}
                onClick={handleAddManualRow}
                style={{ marginTop: 8, textTransform: 'none' }}
              >
                Add Row
              </Button>
              {manualItems.length > 0 && (
                <Typography variant="caption" display="block" style={{ marginTop: 4, color: '#666' }}>
                  {manualItems.length} items = {manualItems.reduce((s, r) => s + (parseInt(r.qty, 10) || 1), 0)} placement points
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Cancel</Button>
          {importTab === 0 && importStep === 1 && (
            <Button onClick={() => { setImportStep(0); setParsedData(null); }}>Back</Button>
          )}
          {importTab === 0 && importStep === 1 && (
            <Button
              variant="contained" color="primary"
              onClick={handleImport}
              disabled={!parsedData?.rows?.length}
            >
              Import {parsedData?.rows?.length || 0} Items
            </Button>
          )}
          {importTab === 1 && parsedPlanItems.length > 0 && (
            <Button
              variant="contained" color="primary"
              onClick={handleImportPlanItems}
            >
              Import {parsedPlanItems.reduce((s, i) => s + (i.qty || 1), 0)} Items
            </Button>
          )}
          {importTab === 2 && manualItems.length > 0 && (
            <Button
              variant="contained" color="primary"
              onClick={handleImportManualItems}
            >
              Import {manualItems.reduce((s, r) => s + (parseInt(r.qty, 10) || 1), 0)} Items
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(PlacementQueue);
